/**
 * HOME PAGE — DTScotslad
 * BUILD-MARKER: home-v2-2026-07-01
 * Sections (top → bottom):
 *   1. Welcome strip (signed-in users see "Welcome back, Mark")
 *   2. Hero
 *   3. Love letter intro
 *   4. Featured clips
 *   5. Aerial Reels (full YouTube gallery)
 *   6. Flight Path of a Survivor (interactive map with 7 pins)
 *   7. Regions
 *   8. Subscribe CTA divider
 *   9. Community Spotlight teaser
 *  10. Quick links
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import VisitedMap from "@/components/VisitedMap";
import MissionBanner from "@/components/MissionBanner";
import HomeTransmissionTeaser from "@/components/HomeTransmissionTeaser";
import PageCTA from "@/components/PageCTA";
import TestimonialsWall from "@/components/TestimonialsWall";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";
import { useContent } from "@/lib/useContent";
import { useAuth } from "@/context/AuthContext";
import usePageMeta from "@/lib/usePageMeta";

// Poster (instant paint) — misty aerial Scottish valley with waterfalls.
const HERO_POSTER = "https://images.pexels.com/photos/19593823/pexels-photo-19593823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920";
// Mark's own drone footage — hosted on the Emergent assets CDN. Autoplays
// muted, loops, plays inline on iOS. Swap URL to update the hero.
const HERO_VIDEO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_drone-vista-scotland/artifacts/g2u7tq7r_0816%281%29.mp4";
const HERO_IMG = HERO_POSTER; // used for SEO ogImage below
const DIVIDER_IMG = "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const BUILD_MARKER = "home-v2-2026-07-01";

export default function Home() {
    usePageMeta({
        title: "DTScotslad — Scotland from four hundred feet",
        description: "A pilot's aerial diary of Scotland — cinematic 4K drone films from the Highlands, Islands and cities, and a live countdown to the NC500 mission for the MS Society.",
        ogImage: HERO_IMG,
    });
    const { user } = useAuth();
    const [featured, setFeatured] = useState([]);
    const [allVideos, setAllVideos] = useState([]);
    const [regions, setRegions] = useState([]);

    const { data: hero } = useContent("home_hero", {
        overline: "A Scottish Aerial Diary · Est. 2026",
        title_line_1: "Scotland,",
        title_line_2: "where my spirit takes flight!",
        body: "Drone films from the Highlands, the Hebrides and the cities in between.",
        cta_primary: "Join the flight log",
        cta_secondary: "Watch the gallery",
    });
    const { data: intro } = useContent("home_intro", {
        overline: "The Pilot",
        title: "A love letter, in 4K.",
        paragraphs: [],
    });
    const { data: cta } = useContent("home_cta_divider", {
        overline: "Behind the cuts",
        title: "Subscribers see the cold takes.",
        body: "",
        button: "Subscribe — £1.89 / month",
    });

    useEffect(() => {
        api.get("/videos", { params: { featured: true } }).then((r) => setFeatured(r.data));
        api.get("/videos").then((r) => setAllVideos(r.data));
        api.get("/videos/regions").then((r) => setRegions(r.data));
    }, []);

    return (
        <div data-testid="home-page" data-build={BUILD_MARKER}>
            {/* 0. CURRENT MISSION BANNER */}
            <MissionBanner />

            {/* 1. WELCOME STRIP — only shown when signed in */}
            {user && (
                <div className="bg-[var(--sb-surface)] border-b border-[var(--sb-border)]" data-testid="welcome-strip">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between gap-4">
                        <div className="text-sm text-[var(--sb-text-dim)]">
                            <span className="font-serif italic text-[var(--sb-brand)]">Welcome back, {user.name?.split(" ")[0] || "friend"}.</span>
                            <span className="ml-2 hidden sm:inline">The sky&rsquo;s still up there.</span>
                        </div>
                        <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-xs uppercase tracking-[0.2em] text-[var(--sb-text-dim)] hover:text-[var(--sb-text)]" data-testid="welcome-strip-cta">
                            {user.role === "admin" ? "Studio" : "Dashboard"} →
                        </Link>
                    </div>
                </div>
            )}

            {/* 2. HERO — cinematic parallax with masked-line reveal */}
            <HeroCinematic hero={hero} />

            {/* 3. LOVE-LETTER INTRO */}
            <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="intro-section">
                <Reveal className="lg:col-span-4">
                    <div className="overline mb-3"><span className="editorial-rule" />{intro.overline}</div>
                    <h2
                        className="font-serif tracking-tight leading-[0.95]"
                        style={{ fontSize: "clamp(2.2rem, 4.4vw, 4rem)" }}
                    >
                        {intro.title}
                    </h2>
                </Reveal>
                <div className="lg:col-span-7 lg:col-start-6 text-[var(--sb-text-dim)] space-y-6 text-lg leading-[1.85]">
                    {(intro.paragraphs || []).map((p, i) => (
                        <Reveal key={i} delay={0.1 + i * 0.06}><p>{p}</p></Reveal>
                    ))}
                    <Reveal delay={0.35}>
                        <Link to="/about" className="inline-flex items-center gap-2 text-[var(--sb-brand)] hover:text-[var(--sb-text)] transition-colors mt-4 group" data-testid="home-about-link">
                            <span className="w-6 h-px bg-[var(--sb-brand)] group-hover:w-10 transition-all duration-500" />
                            Read the story <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* 4. FEATURED CLIPS */}
            <section className="relative py-28 lg:py-36 border-y border-[var(--sb-border)] overflow-hidden" style={{background: "var(--sb-surface)"}} data-testid="featured-section">
                {/* subtle diagonal light-rake for the "sun through the glen" feel */}
                <div aria-hidden className="light-rake" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
                        <Reveal>
                            <div className="overline mb-3"><span className="editorial-rule" />Featured Clips</div>
                            <h2
                                className="font-serif tracking-tight leading-[0.95]"
                                style={{ fontSize: "clamp(2.4rem, 5vw, 4.6rem)" }}
                            >
                                Recently <span className="italic text-[var(--sb-brand)]">flown.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <Link to="/gallery" className="group text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em]">
                                <span className="w-8 h-px bg-[var(--sb-brand)] group-hover:w-14 transition-all duration-500" />
                                All films <ArrowRight size={14} />
                            </Link>
                        </Reveal>
                    </div>
                    {featured.length === 0 ? (
                        <div className="card p-12 text-center text-[var(--sb-text-dim)]">No featured clips yet. Add one in Studio → Videos.</div>
                    ) : (
                        <Reveal delay={0.2}>
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                <div className="lg:col-span-7">
                                    {featured[0] && <VideoCard video={featured[0]} large />}
                                </div>
                                <div className="lg:col-span-5 flex flex-col gap-6">
                                    {featured.slice(1, 3).map((v) => <VideoCard key={v.id} video={v} />)}
                                </div>
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>

            {/* 5. AERIAL REELS */}
            <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-36" data-testid="aerial-reels-section">
                <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
                    <Reveal>
                        <div className="overline mb-3"><span className="editorial-rule" />Aerial Reels</div>
                        <h2
                            className="font-serif tracking-tight max-w-2xl leading-[0.95]"
                            style={{ fontSize: "clamp(2.4rem, 5vw, 4.6rem)" }}
                        >
                            The full <span className="italic text-[var(--sb-brand)]">flight library.</span>
                        </h2>
                        <p className="text-[var(--sb-text-dim)] mt-5 max-w-xl text-lg">Every clip I&rsquo;ve flown, in one place. Tap to play.</p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link to="/gallery" className="group text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em]">
                            <span className="w-8 h-px bg-[var(--sb-brand)] group-hover:w-14 transition-all duration-500" />
                            Open gallery <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
                {allVideos.length === 0 ? (
                    <div className="card p-12 text-center text-[var(--sb-text-dim)]" data-testid="aerial-reels-empty">
                        No reels yet. Add YouTube videos via <Link to="/admin" className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">Studio → Videos</Link>.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="aerial-reels-grid">
                        {allVideos.map((v, i) => (
                            <Reveal key={v.id} delay={0.05 + (i % 4) * 0.06}>
                                <VideoCard video={v} />
                            </Reveal>
                        ))}
                    </div>
                )}
            </section>

            {/* 6. FLIGHT PATH OF A SURVIVOR — interactive map with the 7 pins */}
            <VisitedMap />
            <div className="max-w-4xl mx-auto px-6 lg:px-12 -mt-6 mb-6 text-center" data-testid="visited-map-caption">
                <p className="text-sm text-[var(--sb-text-muted)] italic">
                    A hand-drawn silhouette of Scotland with every place I&rsquo;ve flown pinned in Scotland blue.
                    Hover a pin to see the location; the dashed line traces the order they were captured in.
                </p>
            </div>

            {/* 6b. TRANSMISSION TEASER — Pilot's Log crosslink + live countdown */}
            <HomeTransmissionTeaser />

            {/* 7. REGIONS */}
            <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-36" data-testid="regions-section">
                <Reveal className="mb-14">
                    <div className="overline mb-3"><span className="editorial-rule" />By Region</div>
                    <h2
                        className="font-serif tracking-tight max-w-2xl leading-[0.95]"
                        style={{ fontSize: "clamp(2.4rem, 5vw, 4.6rem)" }}
                    >
                        Three Scotlands,<br />
                        <span className="italic text-[var(--sb-brand)]">three ways to look at them.</span>
                    </h2>
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { name: "Highlands", img: "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", note: "Glen, ridge and loch." },
                        { name: "Islands", img: "https://images.pexels.com/photos/6280898/pexels-photo-6280898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", note: "Skye, Harris, Staffa." },
                        { name: "Cities", img: "https://images.pexels.com/photos/16948691/pexels-photo-16948691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", note: "Edinburgh, Glasgow, Dundee." },
                    ].map((r, i) => {
                        const count = regions.find((x) => x.region === r.name)?.count || 0;
                        return (
                            <Reveal key={r.name} delay={0.08 + i * 0.08}>
                                <Link to={`/gallery?region=${r.name}`} className="group relative block aspect-[4/5] overflow-hidden border border-[var(--sb-border)] hover:border-[var(--sb-brand)] transition-colors" data-testid={`region-card-${r.name.toLowerCase()}`}>
                                    <img src={r.img} alt={r.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08]" />
                                    <div className="absolute inset-0 image-overlay" />
                                    {/* atmospheric mist wash on hover */}
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: "radial-gradient(60% 50% at 50% 60%, rgba(107,167,222,0.15) 0%, rgba(0,0,0,0) 65%)" }} />
                                    <div className="absolute left-6 right-6 bottom-6">
                                        <div className="overline mb-2">{count} {count === 1 ? "Film" : "Films"}</div>
                                        <h3 className="font-serif text-4xl lg:text-5xl text-[var(--sb-text)] tracking-tight leading-none">{r.name}</h3>
                                        <p className="text-sm text-[var(--sb-text-dim)] mt-2">{r.note}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* 8. CTA DIVIDER — cinematic full-bleed */}
            <section className="relative h-[70vh] overflow-hidden grain" data-testid="cta-divider">
                <img src={DIVIDER_IMG} alt="Loch" className="absolute inset-0 w-full h-full object-cover slow-zoom-sw" />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--sb-bg)]/40 via-[var(--sb-bg)]/60 to-[var(--sb-bg)]/85" />
                <div aria-hidden className="light-rake" />
                <div aria-hidden className="highland-breath" />
                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 max-w-4xl mx-auto">
                    <Reveal>
                        <div className="overline mb-4"><span className="editorial-rule" />{cta.overline}</div>
                    </Reveal>
                    <h2
                        className="font-serif tracking-tight max-w-3xl mb-8 leading-[0.95]"
                        style={{ fontSize: "clamp(2.6rem, 6vw, 5.5rem)" }}
                    >
                        <div><MaskedLine delay={0.1}>{cta.title}</MaskedLine></div>
                    </h2>
                    {cta.body && (
                        <Reveal delay={0.35}>
                            <p className="text-[var(--sb-text-dim)] max-w-xl mb-10 text-lg">{cta.body}</p>
                        </Reveal>
                    )}
                    <Reveal delay={0.45}>
                        <Link to="/subscription" className="btn-primary" data-testid="section-subscribe-cta">
                            {cta.button} <ArrowRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* 9. COMMUNITY SPOTLIGHT TEASER */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="community-teaser">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
                    <Reveal className="lg:col-span-7">
                        <div className="overline mb-3"><span className="editorial-rule" />Community Spotlight</div>
                        <h2
                            className="font-serif tracking-tight leading-[0.95]"
                            style={{ fontSize: "clamp(2.2rem, 4.6vw, 4.2rem)" }}
                        >
                            Subscribers tell<br />
                            <span className="italic text-[var(--sb-brand)]">their own story.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
                        <p className="text-[var(--sb-text-dim)] mb-4 text-lg leading-relaxed">A space for the people who paid £1.89 a month to share what they flew, and why it mattered.</p>
                        <Link to="/spotlight" className="group inline-flex items-center gap-2 text-[var(--sb-brand)] hover:text-[var(--sb-text)] text-xs uppercase tracking-[0.28em]" data-testid="home-spotlight-link">
                            <span className="w-8 h-px bg-[var(--sb-brand)] group-hover:w-14 transition-all duration-500" />
                            See the spotlight <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* 10. QUICK LINKS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { to: "/map", icon: MapPin, overline: "Explore", title: "Locations Map", body: "Every flight, pinned on a map of Scotland." },
                    { to: "/things", icon: null, overline: "Plan a trip", title: "Things to do nearby", body: "Hand-picked walks, festivals, and the quietest beach in Britain." },
                    { to: "/blog", icon: null, overline: "Read", title: "The Journal", body: "Field notes, tips, and one stubborn opinion about colour grading." },
                ].map((q, i) => {
                    const Icon = q.icon;
                    return (
                        <Reveal key={q.to} delay={0.1 + i * 0.08}>
                            <Link to={q.to} className="card p-8 group block h-full" data-testid={`quicklink-${q.to.slice(1)}`}>
                                {Icon && <Icon className="text-[var(--sb-brand)] mb-4" />}
                                <div className="overline mb-3">{q.overline}</div>
                                <h3 className="font-serif text-2xl lg:text-3xl mb-3 tracking-tight">{q.title}</h3>
                                <p className="text-[var(--sb-text-dim)] text-sm leading-relaxed">{q.body}</p>
                                <span className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.28em] text-[var(--sb-brand)] group-hover:gap-3 transition-all">
                                    Enter <ArrowRight size={12} />
                                </span>
                            </Link>
                        </Reveal>
                    );
                })}
            </section>

            <PageCTA variant="book" />

            <TestimonialsWall />

            {/* Hidden build marker so we can prove which build is on production */}
            <div style={{display: "none"}} data-build-marker={BUILD_MARKER} id="build-marker">{BUILD_MARKER}</div>
        </div>
    );
}

/* -------- Cinematic hero: parallax image + masked-line title + drift mist -------- */
function HeroCinematic({ hero }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
    const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

    // Mouse-parallax drift: the image nudges a few pixels toward the cursor,
    // giving the still shot a subtle sense of depth. Spring-smoothed so it
    // never feels twitchy. Only active on non-touch pointers.
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
    const sy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });

    const onMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const cx = (e.clientX - rect.left) / rect.width - 0.5;   // -0.5 .. 0.5
        const cy = (e.clientY - rect.top) / rect.height - 0.5;
        mx.set(cx * -18); // invert for a "look-around" feel
        my.set(cy * -12);
    };
    const onMouseLeave = () => { mx.set(0); my.set(0); };

    return (
        <section
            ref={ref}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className="relative min-h-[100svh] w-full overflow-hidden grain"
            style={{ position: "relative" }}
            data-testid="hero-section"
        >
            {/* Parallax image — outer wrapper does scroll-linked Y + scale,
                inner wrapper does mouse-linked drift so the transforms don't collide. */}
            <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
                <motion.div style={{ x: sx, y: sy }} className="absolute inset-[-4%] will-change-transform">
                    <motion.video
                        src={HERO_VIDEO}
                        poster={HERO_POSTER}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        aria-label="Aerial drone footage by DTScotslad"
                        className="w-full h-full object-cover slow-zoom"
                        data-testid="hero-video"
                        animate={{
                            filter: [
                                "saturate(1.05) brightness(1) contrast(1.02)",   // clear blue skies
                                "saturate(0.55) brightness(0.82) contrast(0.94)", // heavy overcast
                                "saturate(1.05) brightness(1) contrast(1.02)",   // back to clear
                            ],
                        }}
                        transition={{ duration: 34, repeat: Infinity, ease: "easeInOut" }}
                    />
                </motion.div>
                {/* WEATHER CYCLE — a slow crossfade between clear blue-sky
                    warmth and a Highland overcast wash. Independent of scroll
                    and mouse; loops every ~34s with easeInOut so the change
                    is felt rather than announced. */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: "linear-gradient(180deg, rgba(180,192,205,0.55) 0%, rgba(150,162,178,0.42) 45%, rgba(120,132,148,0.20) 85%, rgba(0,0,0,0) 100%)",
                        mixBlendMode: "multiply",
                    }}
                    animate={{ opacity: [0.05, 0.85, 0.05] }}
                    transition={{ duration: 34, repeat: Infinity, ease: "easeInOut" }}
                    data-testid="hero-weather-overlay"
                />
                {/* Warm-clear tint that peaks when the overcast lifts — offset
                    exactly out-of-phase, so the two waves cross-fade cleanly. */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: "linear-gradient(180deg, rgba(140,180,220,0.28) 0%, rgba(200,215,230,0.10) 60%, rgba(0,0,0,0) 100%)",
                        mixBlendMode: "screen",
                    }}
                    animate={{ opacity: [0.7, 0.05, 0.7] }}
                    transition={{ duration: 34, repeat: Infinity, ease: "easeInOut" }}
                />
                {/* Layered atmospheric dimming — always dark enough at the base
                    for headline legibility, always breathing at the top. Overlays
                    stay static so only the photograph drifts under the cursor. */}
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--sb-bg)]/50 via-[var(--sb-bg)]/10 to-[var(--sb-bg)]" />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[var(--sb-bg)]/90 via-[var(--sb-bg)]/40 to-transparent" />
                {/* Off-centre spotlight to lift the pilot's-eye focal point */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(60% 50% at 28% 60%, rgba(255,240,220,0.09) 0%, rgba(0,0,0,0) 60%)" }}
                />
                {/* Vignette to hug the frame */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(90% 75% at 50% 45%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)" }}
                />
            </motion.div>

            {/* Wind-drift haze */}
            <div aria-hidden className="highland-breath" />

            {/* Faint highland ridge silhouette above the fold — brushed on so the
                sky ends in mountains, not in a rectangle. */}
            <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{
                    height: "34vh",
                    opacity: 0.55,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "100% 100%",
                    backgroundPosition: "bottom center",
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 500' preserveAspectRatio='none'><path d='M0 420 L120 360 L260 400 L400 320 L560 380 L720 300 L880 360 L1040 280 L1200 340 L1360 260 L1500 320 L1600 300 L1600 500 L0 500 Z' fill='%230b1f3a' opacity='0.9'/></svg>\")",
                    zIndex: 1,
                }}
            />

            {/* Content */}
            <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 min-h-[100svh] flex flex-col justify-end pb-24 lg:pb-32">
                <Link
                    to="/start-here"
                    data-testid="hero-start-here-pill"
                    className="self-start mb-8 inline-flex items-center gap-3 border-2 backdrop-blur-md px-5 py-2.5 text-xs uppercase tracking-[0.28em] transition-all fade-up delay-100 group"
                    style={{
                        borderRadius: 999,
                        borderColor: "#0055A4",
                        background: "rgba(255, 255, 255, 0.85)",
                        color: "#0055A4",
                        fontWeight: 700,
                        boxShadow: "0 0 24px -6px rgba(0, 85, 164, 0.7)",
                    }}
                >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#0055A4", boxShadow: "0 0 8px rgba(0,85,164,0.95)" }} />
                    New here? <span style={{ color: "#0055A4", fontWeight: 800 }}>Start here →</span>
                </Link>
                <div className="overline mb-4" style={{ color: "#0055A4", fontWeight: 700 }}>
                    <span className="editorial-rule" style={{ background: "#0055A4" }} />
                    <MaskedLine delay={0.05}>{hero.overline}</MaskedLine>
                </div>
                <h1
                    className="font-serif font-light tracking-tighter mt-2 mb-8 max-w-5xl"
                    style={{ fontSize: "clamp(2.2rem, 9vw, 9rem)", lineHeight: 0.95 }}
                    data-testid="hero-headline"
                >
                    <div><MaskedLine delay={0.15}>{hero.title_line_1}</MaskedLine></div>
                    <div>
                        <MaskedLine delay={0.35}>
                            <span className="italic" style={{ color: "#0055A4" }}>{hero.title_line_2}</span>
                        </MaskedLine>
                    </div>
                </h1>
                <div className="max-w-xl mb-12">
                    <MaskedLine delay={0.6} className="text-lg lg:text-xl leading-relaxed" >
                        <span style={{ color: "#ffffff" }}>{hero.body}</span>
                    </MaskedLine>
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.95, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-wrap items-center gap-4"
                >
                    <Link to="/register" className="btn-primary btn-hero-pulse" data-testid="hero-cta-button">
                        {hero.cta_primary} <ArrowRight size={18} />
                    </Link>
                    <Link to="/gallery" className="btn-outline" data-testid="hero-learn-more">
                        <PlayCircle size={18} /> {hero.cta_secondary}
                    </Link>
                </motion.div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.1 }}
                className="absolute bottom-6 right-6 lg:right-12 text-[var(--sb-text-muted)] text-xs tracking-[0.28em] uppercase flex items-center gap-2 z-10"
            >
                <span className="w-8 h-px bg-[var(--sb-brand)]" />
                Scroll <ArrowDown size={14} />
            </motion.div>
        </section>
    );
}
